import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CreateChargesDialog, EquipmentChargesDialog } from 'modules/equipment';
import { Skeleton, Stack } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { useFormContext } from 'react-hook-form';
import { QuoteDepositType } from '@trnsact/trnsact-shared-types';
import { CalculatedTotalsByLevel } from 'modules/quote';
import { collectDealBuilderContactsData, mapEquipmentSourceToItem } from '../lib';
import {
  DealBuilderCreditApplicationSource,
  DealBuilderEquipmentSource,
  DealBuilderFormValues,
  DealBuilderStep,
  DealBuilderVOSource,
} from '../types';
import { dealBuilderApi, InventoryEquipmentItem } from '../api';
import { useCustomerContacts, useDealBuilderPersistence, useDealBuilderStepNavigation } from '../hooks';
import { useNotifications } from 'modules/notification';
import { DealBuilderContextProvider, DeliverySubmitRef } from '../context/DealBuilderContext';
import { BuildAQuoteCard } from './components/BuildAQuoteCard';
import { QuoteHistory } from './components/QuoteHistory';
import { DealBuilderStepPlaceholder } from './steps/DealBuilderStepPlaceholder';
import { ContactsStep } from './steps/contacts';
import { EquipmentStep } from './steps/equipment';
import { PricingStep } from './steps/pricing';
import { ModalsKeys, Nullable } from 'global';
import { CreateUpdateEquipmentDialog, UploadImagesForNewEquipmentDialog } from 'modules/equipment';
import { ModalsContainer } from 'components/shared/Modals';

const STEP_COMPONENTS: Record<DealBuilderStep, React.ComponentType> = {
  [DealBuilderStep.Contacts]: ContactsStep,
  [DealBuilderStep.Equipment]: EquipmentStep,
  [DealBuilderStep.Pricing]: PricingStep,
  [DealBuilderStep.FinanceMenu]: DealBuilderStepPlaceholder,
  [DealBuilderStep.ReviewSend]: DealBuilderStepPlaceholder,
};

interface Props {
  vo: Nullable<DealBuilderVOSource>;
  creditApp: Nullable<DealBuilderCreditApplicationSource>;
  equipmentData: Nullable<DealBuilderEquipmentSource[]>;
  activeProposalId: Nullable<string>;
  onRemount: (proposalId?: string) => void;
}

const modalsWithKeys: Partial<Record<ModalsKeys, ReactNode>> = {
  [ModalsKeys.CreateUpdateEquipmentDialog]: <CreateUpdateEquipmentDialog />,
  [ModalsKeys.UploadImagesForNewEquipmentDialog]: <UploadImagesForNewEquipmentDialog />,
  [ModalsKeys.CreateChargesEquipmentDialog]: <CreateChargesDialog />,
  [ModalsKeys.ChargesEquipmentDialog]: <EquipmentChargesDialog />,
};

export const DealBuilderWrapper = ({ vo, creditApp, equipmentData, activeProposalId, onRemount }: Props) => {
  const { setValue } = useFormContext<DealBuilderFormValues>();
  const { viewStep } = useDealBuilderStepNavigation();
  const { showNotification } = useNotifications();
  const [startingNewQuote, setStartingNewQuote] = useState(false);
  const [selectedEquipmentItem, setSelectedEquipmentItem] = useState<InventoryEquipmentItem | null>(null);
  const [calculatedTotals, setCalculatedTotals] = useState<CalculatedTotalsByLevel>({
    invoice: { fees: 0, taxes: 0, price: 0 },
    equipments: { fees: 0, taxes: 0, price: 0 },
    attachments: { fees: 0, taxes: 0, price: 0 },
  });
  const deliverySubmitRef = useRef<Nullable<{ submit: () => Promise<{ isValid: boolean; formValues: any }> }>>(
    null
  ) as DeliverySubmitRef;

  const {
    activeEntity,
    historyEntities,
    loading: dealBuilderLoading,
    initialData,
    ensureDealBuilder,
    startNewQuote: startNewQuoteRaw,
    upsertContacts,
    upsertEquipments,
    updateQuote,
    updateEquipments,
    updateInvoice,
  } = useDealBuilderPersistence(vo?.vendorOpportunityId ?? null, activeProposalId);

  const { contacts, loading: contactsLoading } = useCustomerContacts(vo?.potentialCustomer?.id ?? null);

  const contactsData = useMemo(
    () => collectDealBuilderContactsData(vo, contacts, creditApp?.id ?? null),
    [vo, contacts, creditApp?.id]
  );

  // Auto-populate contacts from backend data or fallback to primary contact from VO
  const hasAutoSelectedPrimary = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedPrimary.current || dealBuilderLoading) return;
    const hasSavedContacts =
      initialData && (initialData.ownerPgIds.length > 0 || initialData.accountContactIds.length > 0);
    if (hasSavedContacts) {
      hasAutoSelectedPrimary.current = true;
      setValue('contacts.ownerPgIds', initialData.ownerPgIds);
      setValue('contacts.accountContactIds', initialData.accountContactIds);
    } else if (contactsData.primaryContact?.ownerPgId) {
      hasAutoSelectedPrimary.current = true;
      setValue('contacts.ownerPgIds', [contactsData.primaryContact.ownerPgId]);
    }
  }, [dealBuilderLoading, initialData, contactsData.primaryContact, setValue]);

  // Seed deposit and calculated totals from quote on first load
  const hasAutoPopulatedQuoteData = useRef(false);
  useEffect(() => {
    if (hasAutoPopulatedQuoteData.current || dealBuilderLoading || !initialData) return;
    hasAutoPopulatedQuoteData.current = true;

    if (initialData.deposit) {
      const { type, value, notes } = initialData.deposit;
      setValue('pricing.customerDeposit.type', type as QuoteDepositType);
      setValue('pricing.customerDeposit.value', value);
      setValue('pricing.customerDeposit.notes', notes ?? '');
    }

    setCalculatedTotals(initialData.calculatedTotals);
  }, [dealBuilderLoading, initialData, setValue]);

  const [fetchEquipmentById] = useLazyQuery<{ equipment: DealBuilderEquipmentSource }, { id: string }>(
    dealBuilderApi.getEquipmentDetailsById
  );

  // Auto-populate equipment from backend data or fallback to first equipment from props
  const hasAutoSelectedEquipment = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedEquipment.current || dealBuilderLoading) return;
    if (initialData?.equipmentId) {
      hasAutoSelectedEquipment.current = true;
      setValue('equipment.selectedId', initialData.equipmentId);
      const source = equipmentData?.find(e => e.equipmentId === initialData.equipmentId);
      if (source) {
        setSelectedEquipmentItem(mapEquipmentSourceToItem(source));
      } else {
        fetchEquipmentById({ variables: { id: initialData.equipmentId } }).then(({ data }) => {
          if (data?.equipment) setSelectedEquipmentItem(mapEquipmentSourceToItem(data.equipment));
        });
      }
    } else if (equipmentData?.length) {
      hasAutoSelectedEquipment.current = true;
      const first = equipmentData[0];
      const mapped = mapEquipmentSourceToItem(first);
      setValue('equipment.selectedId', first.equipmentId);
      setSelectedEquipmentItem(mapped);
    }
  }, [dealBuilderLoading, initialData, equipmentData, setValue, fetchEquipmentById]);

  const handleSetSelectedEquipmentItem = useCallback((item: InventoryEquipmentItem | null) => {
    setSelectedEquipmentItem(item);
  }, []);

  const startNewQuote = useCallback(async () => {
    setStartingNewQuote(true);
    const id = await startNewQuoteRaw();
    if (id) {
      showNotification('New quote created successfully');
      onRemount(id);
    }
    setStartingNewQuote(false);
    return id;
  }, [startNewQuoteRaw, showNotification, onRemount]);

  const setActiveEntity = useCallback(
    (proposalId: string) => {
      onRemount(proposalId);
    },
    [onRemount]
  );

  const StepComponent = STEP_COMPONENTS[viewStep];

  const isInitialLoading = dealBuilderLoading || contactsLoading || startingNewQuote;

  return (
    <DealBuilderContextProvider
      value={{
        vo,
        contactsData,
        contactsLoading,
        equipmentData,
        selectedEquipmentItem,
        setSelectedEquipmentItem: handleSetSelectedEquipmentItem,
        deliverySubmitRef,
        activeEntity,
        historyEntities,
        setActiveEntity,
        ensureDealBuilder,
        startNewQuote,
        upsertContacts,
        upsertEquipments,
        updateQuote,
        updateEquipments,
        updateInvoice,
        calculatedTotals,
        setCalculatedTotals,
      }}
    >
      {isInitialLoading ? (
        <Stack gap={2}>
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
        </Stack>
      ) : (
        <Stack gap={2}>
          <BuildAQuoteCard />
          <StepComponent />
          <QuoteHistory />
        </Stack>
      )}

      <ModalsContainer modalsWithKeys={modalsWithKeys} />
    </DealBuilderContextProvider>
  );
};
